import React, { useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { REACTIONS, togglePassportReaction, getMyReactionsByCv, type ReactionKey } from '../../api/passportReactions';
import { showToast } from '../../utils/errorHandler';

interface PassportReactionBarProps {
  cvId: string;
  apiCall: (url: string, options?: RequestInit) => Promise<Response>;
  /** 'full' = labelled pills (passport header); 'compact' = collapsed trigger + popover (feed cards). */
  variant?: 'full' | 'compact';
  /**
   * Fetch the viewer's existing reactions on mount so the selected state is
   * accurate. Use on the opened passport; skip on feed cards (use
   * `initialReactions` from a single batched fetch there instead).
   */
  loadInitial?: boolean;
  /**
   * Pre-seeded selected reactions (e.g. from a batched feed fetch) so the bar
   * shows what the viewer already sent without an extra per-card request.
   */
  initialReactions?: ReactionKey[];
  /**
   * Notifies the parent of the new selected set after each toggle so a feed can
   * keep its seed in sync (e.g. a card stays correct if it remounts mid-session).
   */
  onChange?: (keys: ReactionKey[]) => void;
  className?: string;
}

/**
 * Low-friction "vibe" reactions on a passport (Slay, Goated, Cooking).
 * Free to use — the passport owner gets a nudge and can start a free chat back.
 * Tapping an active reaction withdraws it (un-react); intent is sent explicitly
 * to the backend so a stale view can never accidentally flip a reaction.
 */
const PassportReactionBar: React.FC<PassportReactionBarProps> = ({
  cvId,
  apiCall,
  variant = 'full',
  loadInitial = false,
  initialReactions,
  onChange,
  className = '',
}) => {
  const [selected, setSelected] = useState<Set<ReactionKey>>(
    () => new Set(initialReactions || []),
  );
  const [busy, setBusy] = useState<ReactionKey | null>(null);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [popStyle, setPopStyle] = useState<React.CSSProperties>({});

  const compact = variant === 'compact';

  // Keep in sync if the batched feed seed arrives/changes after mount.
  useEffect(() => {
    if (initialReactions) setSelected(new Set(initialReactions));
  }, [initialReactions]);

  // Seed selected state from the server (full passport view).
  useEffect(() => {
    if (!loadInitial) return;
    let cancelled = false;
    getMyReactionsByCv(apiCall, cvId)
      .then((keys) => { if (!cancelled) setSelected(new Set(keys)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [loadInitial, cvId, apiCall]);

  const handleToggle = useCallback(
    async (key: ReactionKey, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (busy) return;
      const isOn = selected.has(key);
      const action = isOn ? 'remove' : 'add';
      setBusy(key);

      // Optimistic flip (keep the parent feed seed in sync too)
      const next = new Set(selected);
      if (isOn) next.delete(key);
      else next.add(key);
      setSelected(next);
      onChange?.(Array.from(next));

      try {
        await togglePassportReaction(apiCall, cvId, key, action);
      } catch (err: any) {
        // Revert on failure (local + parent)
        const reverted = new Set(next);
        if (isOn) reverted.add(key);
        else reverted.delete(key);
        setSelected(reverted);
        onChange?.(Array.from(reverted));
        const msg = String(err?.message || '');
        if (msg.includes('429')) showToast('Easy tiger, daily reaction limit reached ✌️', 'error');
        else if (msg.includes('400')) showToast("Can't react to your own passport", 'info');
        else showToast(isOn ? 'Could not remove reaction' : 'Could not save reaction', 'error');
      } finally {
        setBusy(null);
      }
    },
    [apiCall, cvId, selected, busy, onChange],
  );

  // Position the popover (fixed, via portal) relative to the trigger.
  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const MARGIN = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Width fits the single-line content (measured once rendered); fall back to 240.
    const popW = popoverRef.current?.offsetWidth || 240;

    // Align the popover's right edge to the trigger's right edge, clamped.
    let left = rect.right - popW;
    if (left < MARGIN) left = MARGIN;
    if (left + popW > vw - MARGIN) left = Math.max(MARGIN, vw - MARGIN - popW);

    // Prefer opening ABOVE the trigger; only drop below if there isn't room above.
    const estHeight = popoverRef.current?.offsetHeight || 96;
    const openAbove = rect.top > estHeight + MARGIN;

    const style: React.CSSProperties = {
      position: 'fixed',
      left: Math.round(left),
      maxWidth: `calc(100vw - ${MARGIN * 2}px)`,
      zIndex: 10050,
    };
    if (openAbove) style.bottom = Math.round(vh - rect.top + MARGIN);
    else style.top = Math.round(rect.bottom + MARGIN);
    setPopStyle(style);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    // Recompute once more after the popover has rendered (for height-aware flip).
    const raf = requestAnimationFrame(reposition);
    return () => cancelAnimationFrame(raf);
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => reposition();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, reposition]);

  // ── FULL variant: unchanged labelled pill row ──────────────────────────────
  if (!compact) {
    return (
      <div
        className={`flex items-center gap-1.5 flex-wrap ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {REACTIONS.map((r) => {
          const isOn = selected.has(r.key);
          return (
            <motion.button
              key={r.key}
              type="button"
              whileTap={{ scale: 0.88 }}
              disabled={busy === r.key}
              onClick={(e) => handleToggle(r.key, e)}
              title={isOn ? `${r.label} (tap to undo)` : (r.label || 'React')}
              className={`flex items-center gap-1 rounded-full font-semibold transition-all border px-1.5 py-0.5 text-[10px] sm:px-2.5 sm:py-1.5 sm:text-xs ${
                isOn
                  ? 'bg-pink-500 text-white border-pink-500 shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-pink-300 hover:text-pink-600'
              } ${busy === r.key ? 'opacity-60' : ''}`}
            >
              <span className="text-[11px] sm:text-sm leading-none">{r.emoji}</span>
              {r.label && <span>{r.label}</span>}
            </motion.button>
          );
        })}
      </div>
    );
  }

  // ── COMPACT variant: collapsed trigger + portal popover ─────────────────────
  const selectedKeys = REACTIONS.filter((r) => selected.has(r.key));
  const hasSelection = selectedKeys.length > 0;

  return (
    <div className={`relative inline-flex ${className}`} onClick={(e) => e.stopPropagation()}>
      <motion.button
        ref={triggerRef}
        type="button"
        whileTap={{ scale: 0.9 }}
        aria-label="React"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen((o) => !o); }}
        className={`flex items-center justify-center gap-0.5 rounded-full border transition-all ${
          hasSelection
            ? 'h-6 min-w-[24px] px-1.5 text-xs bg-pink-500 text-white border-pink-500 shadow-sm'
            : 'h-6 w-6 border-dashed border-slate-300 text-gray-400 hover:border-pink-300 hover:text-pink-400'
        }`}
      >
        {hasSelection ? (
          <span className="flex items-center leading-none text-[12px]">
            {selectedKeys.map((r) => (
              <span key={r.key} className="leading-none">{r.emoji}</span>
            ))}
          </span>
        ) : (
          <Plus className="w-3.5 h-3.5" />
        )}
      </motion.button>

      {open && ReactDOM.createPortal(
        <div
          ref={popoverRef}
          style={popStyle}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className="rounded-2xl border border-slate-200 bg-white shadow-xl p-2 sm:p-2.5 w-max"
        >
          <div className="flex items-center gap-1.5 flex-nowrap">
            {REACTIONS.map((r) => {
              const isOn = selected.has(r.key);
              return (
                <motion.button
                  key={r.key}
                  type="button"
                  whileTap={{ scale: 0.88 }}
                  disabled={busy === r.key}
                  onClick={(e) => handleToggle(r.key, e)}
                  title={isOn ? `${r.label} (tap to undo)` : (r.label || 'React')}
                  className={`flex items-center gap-1 rounded-full font-semibold transition-all border px-2 py-1 text-[11px] sm:px-2.5 sm:py-1.5 sm:text-xs ${
                    isOn
                      ? 'bg-pink-500 text-white border-pink-500 shadow-sm'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-pink-300 hover:text-pink-600'
                  } ${busy === r.key ? 'opacity-60' : ''}`}
                >
                  <span className="text-[13px] sm:text-sm leading-none">{r.emoji}</span>
                  {r.label && <span>{r.label}</span>}
                </motion.button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default PassportReactionBar;
